// src/components/patient/appointments/CancelAppointmentDialog.jsx
//
// Cancellation in an application modal. The server decides everything that
// matters (whether cancelling is allowed, the pinned policy, the financial
// follow-up); this dialog only collects an optional/required reason and shows
// the server's outcome. Double submits are ignored while a request runs.

import React, { useRef, useState } from "react";

import { readApiError } from "../../../lib/apiErrors";
import { appointmentWorkspaceApi } from "../../../services/patientAppointmentWorkspace";

import Dialog from "./Dialog";

const REASON_MAX = 2000;

export default function CancelAppointmentDialog({ open, appointment, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const inFlight = useRef(false);

  const required = Boolean(appointment?.allowed_actions?.cancel_reason_required);
  const preview = appointment?.cancellation_preview;
  const policy = appointment?.cancellation_policy;

  function close() {
    if (busy) return;
    setError("");
    setFieldError("");
    onClose?.();
  }

  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    const trimmed = reason.trim();
    if (required && !trimmed) {
      setFieldError("Please tell us why you are cancelling.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError("");
    setFieldError("");
    try {
      const result = await appointmentWorkspaceApi.cancel(appointment.id, trimmed);
      setReason("");
      onCancelled?.(result);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.field === "reason") setFieldError(readApiError(err));
      else setError(readApiError(err, "The appointment could not be cancelled. Please try again."));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} title="Cancel appointment" labelId="cancel-appointment-title" busy={busy} onClose={close}>
      <form onSubmit={submit} noValidate>
        <p className="text-sm text-slate-700">
          {appointment?.service_name} with {appointment?.doctor_name}. This cannot be undone.
        </p>

        {policy?.has_policy ? (
          <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="cancellation-policy">
            <p className="font-semibold">Cancellation policy</p>
            <p className="mt-1">
              Free cancellation up to {policy.window_hours} hour{policy.window_hours === 1 ? "" : "s"} before the appointment.
              {policy.fee_type !== "none" ? " A late cancellation may be reviewed by the clinic." : ""}
            </p>
            {policy.notes ? <p className="mt-1">{policy.notes}</p> : null}
          </div>
        ) : null}
        {preview && preview.within_free_window === false && preview.patient_fee_would_apply ? (
          <p className="mt-2 text-xs font-semibold text-amber-800">You are inside the policy window. The clinic will review any fee; nothing is charged automatically.</p>
        ) : null}

        <div className="mt-4">
          <label htmlFor="cancel-reason" className="text-sm font-semibold">
            Reason {required ? <span aria-hidden="true" className="text-rose-600">*</span> : <span className="font-normal text-slate-500">(optional)</span>}
            {required ? <span className="sr-only"> (required)</span> : null}
          </label>
          <textarea
            id="cancel-reason"
            data-autofocus
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            aria-invalid={fieldError ? "true" : undefined}
            aria-describedby={fieldError ? "cancel-reason-error" : "cancel-reason-hint"}
            aria-required={required || undefined}
            className="mt-2 min-h-[96px] w-full rounded-2xl border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
          />
          <p id="cancel-reason-hint" className="mt-1 text-xs text-slate-500">Shared only with your care team.</p>
          {fieldError ? <p id="cancel-reason-error" role="alert" className="mt-1 text-xs font-semibold text-rose-700">{fieldError}</p> : null}
        </div>

        {error ? <p role="alert" className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={close} disabled={busy} className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
            Keep appointment
          </button>
          <button type="submit" disabled={busy} aria-busy={busy || undefined} className="rounded-2xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? "Cancelling…" : "Cancel appointment"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
